import { findR } from '@execonline-inc/collections';
import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';
import { identity } from '@kofno/piper';
import { dateISO, Decoder, field, number, oneOf, string, succeed } from 'jsonous';
import { nonEmptyList, readonlyArray } from '../../../Decoders';
import { resourceDecoder } from '../../../Resource/Decoders';
import {
  ExpirationAlert,
  InviteMoreParticipantsAlert,
  kind,
  LicenseInsight,
  OverallocationAlert,
  ProductLicenseOverview,
  ProductLicensesOverviewPayload,
  ProductLicensesOverviewResource,
  Upsell,
  UpsellResource,
} from './Types';

const licenseKindDecoder: Decoder<kind> = oneOf([
  stringLiteral<kind>('active'),
  stringLiteral<kind>('future'),
  stringLiteral<kind>('past'),
]);

export const productLicenseOverviewDecoder: Decoder<ProductLicenseOverview> = succeed({})
  .assign('id', field('id', number))
  .assign('organizationName', field('organization_name', string))
  .assign('name', field('name', string))
  .assign('publicName', field('public_name', explicitMaybe(string)))
  .assign('licenseQuantity', field('license_quantity', number))
  .assign('startOn', field('start_on', dateISO))
  .assign('endOn', field('end_on', dateISO))
  .assign('daysRemaining', field('days_remaining', number))
  .assign('daysFromNow', field('days_from_now', number))
  .assign('allocatedCount', field('allocated_count', number))
  .assign('activatedCount', field('activated_count', number))
  .assign('provisionedCount', field('provisioned_count', number))
  .assign('activatableCount', field('activatable_count', number))
  .assign('licenseTypeId', field('license_type_id', number))
  .assign('licenseKind', field('kind', licenseKindDecoder));

export const productLicenseOverviewFromIdDecoder = (
  productLicenses: ReadonlyArray<ProductLicenseOverview>,
): Decoder<ProductLicenseOverview> =>
  number.andThen(
    (id) =>
      new Decoder<ProductLicenseOverview>(() =>
        findR((l: ProductLicenseOverview) => l.id === id)(productLicenses).mapError(
          () => `Could not find product license overview for license #${id}`,
        ),
      ),
  );

export const overallocationAlertDecoder = (
  productLicenses: ReadonlyArray<ProductLicenseOverview>,
): Decoder<OverallocationAlert> =>
  succeed({})
    .assign('kind', field('kind', stringLiteral('overallocation-alert')))
    .assign(
      'productLicenseOverviews',
      field(
        'product_license_ids',
        nonEmptyList(productLicenseOverviewFromIdDecoder(productLicenses)),
      ),
    )
    .assign('overallocatedBy', field('overallocated_by', number))
    .assign('key', ({ kind, productLicenseOverviews: { first } }) => succeed(kind + first.id));

export const expirationAlertDecoder = (
  productLicenses: ReadonlyArray<ProductLicenseOverview>,
): Decoder<ExpirationAlert> =>
  succeed({})
    .assign('kind', field('kind', stringLiteral('expiration-alert')))
    .assign(
      'productLicenseOverviews',
      field(
        'product_license_ids',
        nonEmptyList(productLicenseOverviewFromIdDecoder(productLicenses)),
      ),
    )
    .assign('key', ({ kind, productLicenseOverviews: { first } }) => succeed(kind + first.id));

export const inviteMoreParticipantsAlertDecoder = (
  productLicenses: ReadonlyArray<ProductLicenseOverview>,
): Decoder<InviteMoreParticipantsAlert> =>
  succeed({})
    .assign('kind', field('kind', stringLiteral('invite-more-participants-alert')))
    .assign(
      'productLicenseOverviews',
      field(
        'product_license_ids',
        nonEmptyList(productLicenseOverviewFromIdDecoder(productLicenses)),
      ),
    )
    .assign('key', ({ kind, productLicenseOverviews: { first } }) => succeed(kind + first.id));

export const licenseInsightDecoder = (
  productLicenses: ReadonlyArray<ProductLicenseOverview>,
): Decoder<LicenseInsight> =>
  oneOf([
    overallocationAlertDecoder(productLicenses).map<LicenseInsight>(identity),
    expirationAlertDecoder(productLicenses).map<LicenseInsight>(identity),
    inviteMoreParticipantsAlertDecoder(productLicenses).map<LicenseInsight>(identity),
  ]);

const upsellDecoder: Decoder<Upsell> = succeed({})
  .assign('id', field('id', number))
  .assign('title', field('title', string));
const upsellResourceDecoder: Decoder<UpsellResource> = resourceDecoder(upsellDecoder);

export const productLicensesOverviewPayloadDecoder: Decoder<ProductLicensesOverviewPayload> =
  succeed({})
    .assign('licensesPurchased', field('licenses_purchased', number))
    .assign('licensesActivatedRatio', field('licenses_activated_ratio', number))
    .assign('licensesAvailable', field('licenses_available', number))
    .assign(
      'productLicenseOverviews',
      field('product_license_overviews', readonlyArray(productLicenseOverviewDecoder)),
    )
    .assign('licenseInsights', ({ productLicenseOverviews }) =>
      field('license_insights', readonlyArray(licenseInsightDecoder(productLicenseOverviews))),
    )
    .assign('upsells', field('upsells', readonlyArray(upsellResourceDecoder)));

export const productLicensesOverviewResourceDecoder: Decoder<ProductLicensesOverviewResource> =
  resourceDecoder(productLicensesOverviewPayloadDecoder);
